/**
 * StepPagamento — Step 5: Pagamento
 * Selezione metodo di pagamento: Carta di credito (Stripe) o Bonifico bancario.
 * Design premium A-Sight con animazioni e micro-interazioni.
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CreditCard, Building2, ShieldCheck, Lock, Clock, Zap, Globe,
  CheckCircle2, Sparkles, ArrowRight, Shield,
} from 'lucide-react';

const METHODS = [
  {
    id: 'stripe',
    label: 'Carta di credito / debito',
    subtitle: 'Pagamento immediato e sicuro',
    description: 'Verrai reindirizzato a Stripe per completare il pagamento in sicurezza.',
    icon: CreditCard,
    badge: 'Consigliato',
    gradient: 'from-yellow-500 to-amber-500',
    lightBg: 'bg-gradient-to-br from-yellow-50 to-amber-50',
    borderActive: 'border-yellow-400 ring-2 ring-yellow-200',
    features: [
      { icon: Zap, text: 'Attivazione immediata', highlight: true },
      { icon: Lock, text: 'Crittografia SSL 256-bit' },
      { icon: Globe, text: 'Visa, Mastercard, AmEx, SEPA' },
    ],
    brands: ['visa', 'mastercard', 'amex'],
  },
  {
    id: 'bonifico',
    label: 'Bonifico bancario',
    subtitle: 'Pagamento tradizionale',
    description: 'Riceverai le coordinate bancarie via email. Il servizio sarà attivato dopo la ricezione.',
    icon: Building2,
    badge: null,
    gradient: 'from-gray-400 to-gray-500',
    lightBg: 'bg-gradient-to-br from-gray-50 to-slate-50',
    borderActive: 'border-gray-400 ring-2 ring-gray-200',
    features: [
      { icon: Clock, text: 'Attivazione entro 2-3 giorni lavorativi' },
      { icon: ShieldCheck, text: 'Pagamento via home banking' },
    ],
    brands: [],
  },
];

/* Card brands SVG-style pills */
function CardBrands() {
  const brands = [
    { name: 'Visa', bg: 'bg-[#1A1F71]', text: 'text-white', label: 'VISA' },
    { name: 'Mastercard', bg: 'bg-[#EB001B]', text: 'text-white', label: 'MC' },
    { name: 'AmEx', bg: 'bg-[#006FCF]', text: 'text-white', label: 'AMEX' },
    { name: 'SEPA', bg: 'bg-[#2C6DB5]', text: 'text-white', label: 'SEPA' },
  ];
  return (
    <div className="flex items-center gap-1.5 mt-3">
      {brands.map((b) => (
        <span
          key={b.name}
          className={`${b.bg} ${b.text} text-[9px] font-bold px-2 py-0.5 rounded tracking-wider`}
        >
          {b.label}
        </span>
      ))}
    </div>
  );
}

export default function StepPagamento({ paymentMethod, onChangePaymentMethod, errors, stripeAvailable = false }) {
  const [hoveredId, setHoveredId] = useState(null);
  const visibleMethods = stripeAvailable ? METHODS : METHODS.filter(m => m.id !== 'stripe');

  return (
    <div className="h-full flex flex-col overflow-y-auto pr-1">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative">
          <div className="p-2.5 bg-gradient-to-br from-yellow-100 to-amber-100 rounded-xl shadow-sm">
            <CreditCard className="w-6 h-6 text-yellow-600" />
          </div>
          <Sparkles className="w-3.5 h-3.5 text-yellow-500 absolute -top-1 -right-1" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Metodo di Pagamento</h2>
          <p className="text-sm text-gray-500">Scegli come completare il tuo ordine</p>
        </div>
      </div>

      {/* Payment method cards */}
      <div className="space-y-4 flex-1">
        {visibleMethods.map((method) => {
          const selected = paymentMethod === method.id;
          const hovered = hoveredId === method.id;
          const Icon = method.icon;

          return (
            <motion.button
              key={method.id}
              type="button"
              onClick={() => onChangePaymentMethod(method.id)}
              onMouseEnter={() => setHoveredId(method.id)}
              onMouseLeave={() => setHoveredId(null)}
              whileTap={{ scale: 0.995 }}
              className={`
                w-full text-left rounded-2xl border-2 p-5 transition-all duration-300 relative overflow-hidden
                ${selected
                  ? `${method.borderActive} ${method.lightBg} shadow-lg`
                  : `border-gray-200 bg-white ${hovered ? 'border-gray-300 shadow-md' : 'shadow-sm'}`}
              `}
            >
              {/* Subtle gradient accent bar */}
              <div className={`
                absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${method.gradient}
                transition-opacity duration-300
                ${selected ? 'opacity-100' : 'opacity-0'}
              `} />

              <div className="flex items-start gap-4">
                {/* Custom radio */}
                <div className="mt-0.5 relative">
                  <div className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                    transition-all duration-300
                    ${selected
                      ? 'border-yellow-500 bg-yellow-500 shadow-sm shadow-yellow-200'
                      : 'border-gray-300 bg-white'}
                  `}>
                    <AnimatePresence>
                      {selected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                          transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                        >
                          <CheckCircle2 className="w-5 h-5 text-white" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Icon */}
                <div className={`
                  p-3 rounded-xl flex-shrink-0 transition-all duration-300
                  ${selected
                    ? `bg-gradient-to-br ${method.gradient} shadow-md`
                    : hovered
                      ? 'bg-gray-200'
                      : 'bg-gray-100'}
                `}>
                  <Icon className={`w-6 h-6 ${selected ? 'text-white' : 'text-gray-400'}`} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`font-bold text-base ${selected ? 'text-gray-900' : 'text-gray-700'}`}>
                      {method.label}
                    </span>
                    {method.badge && (
                      <motion.span
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wide bg-green-100 text-green-700 border border-green-200"
                      >
                        <Sparkles className="w-2.5 h-2.5" />
                        {method.badge}
                      </motion.span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 font-medium mb-1">{method.subtitle}</p>
                  <p className="text-sm text-gray-500 leading-relaxed">{method.description}</p>

                  {/* Features */}
                  <AnimatePresence>
                    <motion.div
                      className="mt-3 space-y-2"
                      initial={false}
                      animate={{ opacity: selected ? 1 : 0.4 }}
                      transition={{ duration: 0.3 }}
                    >
                      {method.features.map((feat, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className={`
                            w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0
                            ${selected
                              ? feat.highlight
                                ? 'bg-green-100'
                                : 'bg-yellow-100'
                              : 'bg-gray-100'}
                          `}>
                            <feat.icon className={`w-3 h-3 ${
                              selected
                                ? feat.highlight
                                  ? 'text-green-600'
                                  : 'text-yellow-600'
                                : 'text-gray-400'
                            }`} />
                          </div>
                          <span className={`text-xs ${
                            selected && feat.highlight
                              ? 'text-green-700 font-semibold'
                              : 'text-gray-600'
                          }`}>
                            {feat.text}
                          </span>
                        </div>
                      ))}
                    </motion.div>
                  </AnimatePresence>

                  {/* Card brands for Stripe */}
                  {method.id === 'stripe' && selected && <CardBrands />}
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Security footer */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-5 flex items-center justify-center gap-4 text-[11px] text-gray-400 py-3 border-t border-gray-100"
      >
        <div className="flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5" />
          <span>Pagamenti sicuri</span>
        </div>
        <span className="w-px h-3 bg-gray-200" />
        <div className="flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5" />
          <span>Dati protetti SSL</span>
        </div>
        <span className="w-px h-3 bg-gray-200" />
        <span className="font-medium text-gray-500">Powered by Stripe</span>
      </motion.div>

      {/* Error */}
      {errors?.payment_method && (
        <p className="mt-2 text-red-500 text-sm">{errors.payment_method}</p>
      )}
    </div>
  );
}
